import React from 'react';
import {useFetchPaymentWay, useMarkDeletePaymentWay, useSetPaymentWayPrimary, type PaymentWay} from '@rocket-ui/lk-api';
import {useIsMaxSm} from 'features/auth/ui/AuthorizationSteps/useIsMaxSm';
import {RegistrationButton} from 'features/registration';
import {Drawer} from 'shared/ui/Drawer';
import {PAYMENT_WAY_PRIMARY_HINT, mapPaymentWayToDetails} from '../../model/paymentWaysPreview';
import styles from './PaymentWayDetailsDrawer.module.css';

export type PaymentWaySelection = {
	type: PaymentWay['type'];
	id: number;
};

export type PaymentWayDetailsDrawerProps = {
	selection: PaymentWaySelection | null;
	onClose: () => void;
};

function BankBuildingIcon(): React.ReactElement {
	return (
		<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
			<path d="M3 10L12 4L21 10" stroke="currentColor" strokeWidth="1.6" />
			<path d="M5 10V19H19V10" stroke="currentColor" strokeWidth="1.6" />
			<path d="M3 19H21" stroke="currentColor" strokeWidth="1.6" />
			<path d="M9 19V14H15V19" stroke="currentColor" strokeWidth="1.6" />
		</svg>
	);
}

function TrashIcon(): React.ReactElement {
	return (
		<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
			<rect x="4" y="8" width="20" height="2" fill="black" />
			<rect x="10" y="4" width="8" height="2" fill="black" />
			<path d="M20.8467 9L18.8477 23H9.15234L7.15332 9H20.8467Z" stroke="black" strokeWidth="2" />
		</svg>
	);
}

export function PaymentWayDetailsDrawer({selection, onClose}: PaymentWayDetailsDrawerProps): React.ReactElement {
	const isMaxSm = useIsMaxSm();
	const open = Boolean(selection);
	const {data, isLoading, isError} = useFetchPaymentWay(selection, {enabled: open});
	const setPrimaryMutation = useSetPaymentWayPrimary();
	const deleteMutation = useMarkDeletePaymentWay();
	const [actionError, setActionError] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!open) {
			setActionError(null);
		}
	}, [open]);

	const details = data ? mapPaymentWayToDetails(data) : null;
	const isBusy = setPrimaryMutation.isLoading || deleteMutation.isLoading;
	const showPrimaryButton = Boolean(details?.canBecomePrimary);

	const handleSetPrimary = () => {
		if (!selection || isBusy) {
			return;
		}

		setActionError(null);
		setPrimaryMutation.mutate(selection, {
			onError: () => {
				setActionError('Не получилось сделать способ основным. Попробуйте ещё раз');
			},
		});
	};

	const handleDelete = () => {
		if (!selection || isBusy) {
			return;
		}

		setActionError(null);
		deleteMutation.mutate(selection, {
			onSuccess: () => {
				onClose();
			},
			onError: () => {
				setActionError('Не получилось удалить способ оплаты. Попробуйте ещё раз');
			},
		});
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			showCloseButton
			variant={isMaxSm ? 'fullscreen' : 'side'}
			bodyClassName={styles.drawerBody}
		>
			{isLoading ? <p className={styles.stateText}>Загрузка реквизитов...</p> : null}
			{isError ? <p className={styles.errorText}>Не удалось загрузить реквизиты</p> : null}

			{details ? (
				<>
					<div className={styles.hero}>
						<div className={styles.titleRow}>
							<h2 className={styles.title}>{details.title}</h2>
							<button
								type="button"
								className={styles.deleteButton}
								aria-label="Удалить способ оплаты"
								disabled={isBusy}
								onClick={handleDelete}
							>
								<TrashIcon />
							</button>
						</div>
						<span className={styles.heroIcon} aria-hidden>
							{details.iconUrl ? (
								<img className={styles.heroIconImage} src={details.iconUrl} alt="" />
							) : (
								<BankBuildingIcon />
							)}
						</span>
					</div>

					<div className={styles.scrollContent}>
						<div className={styles.fields}>
							{details.fields.map((field) => (
								<div key={field.label} className={styles.infoBlock}>
									<p className={styles.label}>{field.label}</p>
									<p className={styles.value}>{field.value}</p>
								</div>
							))}
						</div>

						<p className={styles.hint}>{PAYMENT_WAY_PRIMARY_HINT}</p>

						{showPrimaryButton ? (
							<div className={styles.footer}>
								{actionError ? <p className={styles.actionError}>{actionError}</p> : null}
								<RegistrationButton
									variant="muted"
									className={styles.primaryButton}
									loading={setPrimaryMutation.isLoading}
									disabled={isBusy}
									onClick={handleSetPrimary}
								>
									{details.primaryButtonLabel}
								</RegistrationButton>
							</div>
						) : actionError ? (
							<p className={styles.actionError}>{actionError}</p>
						) : null}
					</div>
				</>
			) : null}
		</Drawer>
	);
}
